// apolo/src/lib/loop/spinePredicates.js — E4-WS2-T01 (code-tab-to-9).
// @arch:pure-engine
//
// PURE predicates extracted from inline closures in runLocalAgentLoopInner.
// No React, no window, no fetch, no D1 — framework-free leaf (L-1).

/**
 * Does a tool-result body indicate an error?
 * @param {string} content
 * @returns {boolean}
 */
export function resultIsError(content) {
  if (typeof content !== 'string') return false;
  const s = content.trim();
  return /^(Erro|Comando bloqueado|Bloqueado\/erro|Erro ao executar|Aviso:)/i.test(s) || /^exitCode:\s*[1-9]/.test(s);
}

/**
 * Is the tool result a generic tool-execution error?
 * @param {string} text
 * @returns {boolean}
 */
export function isToolError(text) {
  return /^(Erro|Comando bloqueado|Bloqueado\/erro|Erro ao executar|Aviso:)/i.test(String(text || '').trim());
}

/**
 * Is the error about missing/empty required arguments?
 * @param {string} text
 * @returns {boolean}
 */
export function isMissingArgsError(text) {
  return /precisa de pattern|precisa de path|precisa de command|root está vazio|evite raiz vazia/i.test(String(text || ''));
}

/**
 * Is the error a configuration/IPC error (root missing, IPC unavailable)?
 * @param {string} text
 * @returns {boolean}
 */
export function isConfigToolError(text) {
  const str = String(text || '');
  return /root is required|root está vazio|precisa de um diretório raiz|IPC do Apolo indisponível/i.test(str);
}

// F13-E7-T09 — chat SEM pasta roda: helpers puros do caminho rootless (fora do
// spine pra manter o dp-loc no baseline).
/** True quando a sessão é de CHAT e não há root absoluto (roda sem worktree). */
export function isChatWithoutRoot(sessionKind, root) {
  return sessionKind === 'chat' && (!root || !String(root).trim());
}

/** O que a tool de FS devolve no chat sem pasta, no lugar do erro de config
 * (não começa com "Erro" → não conta pro spiral nem vira stop 'no_root'). */
const NO_FOLDER_CHAT_HINT = [
  'Esta sessão de chat não tem pasta anexada — ferramentas de arquivo/comando estão indisponíveis.',
  'Responda a partir da conversa e do seu conhecimento; se a tarefa exigir editar arquivos,',
  'peça ao usuário pra escolher a pasta (botão Pasta do composer).',
].join(' ');

/** Erro de config em chat rootless vira HINT conversacional; senão passa direto. */
export function rootlessChatToolResult(chatWithoutRoot, result) {
  return chatWithoutRoot && isConfigToolError(result) ? NO_FOLDER_CHAT_HINT : result;
}

/**
 * Normalize content into a signature for identical-content detection.
 * @param {string} v
 * @returns {string}
 */
export function contentSig(v) {
  return String(v || '').trim().replace(/\s+/g, ' ').toLowerCase().slice(0, 2000);
}

/**
 * Build this turn's tool-result evidence from the live conversation.
 * @param {any[]} convo
 * @param {Map} toolNameById
 * @returns {Array<{ id: string, name: string, is_error: boolean }>}
 */
export function turnToolResultsForAudit(convo, toolNameById, toolArgsById) {
  return convo
    .filter((m) => m.role === 'tool')
    .map((m) => {
      const args = toolArgsById?.get(m.tool_call_id);
      return {
        id: m.tool_call_id,
        name: toolNameById.get(m.tool_call_id) || '',
        is_error: resultIsError(m.content),
        // Auditoria 07-06: o auditor de claims precisa VER o comando pra saber se
        // um run_command exit-0 é um check de verdade (vitest) ou um `ls`.
        command: typeof args === 'object' && args !== null ? String(args.command || args.cmd || '') : '',
      };
    });
}

/**
 * Derive the check commands (test/lint/build/etc.) that ran this turn, from the
 * conversation's tool-call arguments. Pure: reads the convo + a nameById map.
 * @param {any[]} convo
 * @param {Map<string, string>} toolNameById
 * @param {Map<string, any>} toolArgsById  — id → parsed arguments (for run_command)
 * @returns {string[]}
 */
export function deriveCheckCommands(convo, toolNameById, toolArgsById) {
  const cmds = [];
  for (const m of convo) {
    if (m.role !== 'tool') continue;
    const name = toolNameById.get(m.tool_call_id) || '';
    if (name !== 'run_command') continue;
    if (resultIsError(m.content)) continue; // errored run doesn't count as a check
    const args = toolArgsById?.get(m.tool_call_id);
    const cmd = typeof args === 'object' && args !== null
      ? String(args.command || args.cmd || '')
      : '';
    if (cmd) cmds.push(cmd);
  }
  return cmds;
}
